/** @format */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import FormResponse from '../form-response';
import FormFields from '../fields';
import RepeatingFieldCollection from '../fields/repeating-field-collection';
import { FormProperties } from '../form-modals';
import Translate from '@components/translation/translate-text-wrapper';
import { useSafeTranslate } from '@hooks/use-safe-translate';
import {
  submitMarketoForm,
  submitFormRequest,
  trackHeapForm,
  checkForMultiSelectFields,
  getFormEmail,
} from '../form-services';
import { getUrlParameter } from '@services/url-services';
import { htmlToCharacterCheck } from '@services/string-transformation-services';
import ModalWrapper from '@components/modals/modal-wrapper';
import { safeCurrentLanguage } from '@services/drupal-services';
import ChineseDisclaimer from '../fields/chinese-disclaimer';
import PrivacyDisclaimer from '../fields/privacy-disclaimer';
import CareersDisclaimer from '@forms/form-types/careers-form/careers-disclaimer';

/**
 * Modal Form Container.
 *
 * @param props
 * @returns
 */
const ModalFormContainer = (props: FormProperties): JSX.Element => {
  const {
    formTitle,
    formDescription,
    formFields,
    formId,
    formType,
    productFormType,
    redirectUrl,
    submitButtonText,
    submitResponse,
    emailTo,
    emailSubject,
    replyToField,
    repeatingFields,
    repeatingMax,
    repeatingMin,
    repeatingAddButtonText,
    repeatingRemoveButtonText,
    repeatingTitle,
    repeatingArrayKey,
    requestType,
    requestUrl,

    // Modal only variables.
    linkText,
    linkClasses,
    region,
    hideLink,

    // Email variables.
    emailRedirect,
    responseMessage,
    emailTitle,
  } = props;

  const [submissionSuccessful, setSubmissionSuccessful] =
    useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({ mode: 'onChange' });
  const translatedSubmitButtonText = useSafeTranslate(submitButtonText, true);
  const translatedSubmit = useSafeTranslate('Submit', true);
  const submit = translatedSubmitButtonText
    ? translatedSubmitButtonText
    : translatedSubmit;
  const title = htmlToCharacterCheck(useSafeTranslate(formTitle, true));

  // Modal only variables.
  const buttonText = useSafeTranslate(linkText, true);
  const [showModal, setShowModal] = useState<boolean>(false);

  /**
   * Submit Request Form.
   */
  const submitForm = (data) => {
    setSubmissionSuccessful(true);

    trackHeapForm({
      title: formTitle,
      type: requestType,
      id: formType,
      region: region,
      location: window.location.href,
      submissionType: 'Form Submissions',
      marketoId: formId,
      productId: productFormType,
      data: data,
    });

    if (requestType === 'email' || requestType === 'axios') {
      const safeEmailTo = getFormEmail(
        emailRedirect ?? false,
        emailTo ?? '',
        formFields,
        data
      );

      const submissionData = {
        data: data,
        formFields: formFields,
        emailTo: safeEmailTo,
        emailTitle: emailTitle,
        subject: emailSubject,
        replyToField: replyToField,
        requestType: requestType,
        requestUrl: requestUrl,
        responseMessage: responseMessage,
      };
      submitFormRequest(submissionData);
    } else {
      submitMarketoForm(formType ?? '', checkForMultiSelectFields(data));
    }

    if (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 500);
    }
    reset();
  };

  /**
   * Open Form.
   */
  const openForm = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();

    trackHeapForm({
      title: formTitle,
      type: requestType,
      id: formType,
      region: region,
      location: window.location.href,
      submissionType: 'Forms Opened',
      marketoId: formId,
      productId: productFormType,
    });

    setShowModal(true);
  };

  /**
   * Close Form.
   */
  const closeForm = (
    event: React.MouseEvent<HTMLElement, MouseEvent> | null
  ) => {
    if (event) {
      event.preventDefault();
    }
    reset();
    setSubmissionSuccessful(false);
    setShowModal(false);
  };

  /**
   * Url Form Check.
   */
  useEffect(() => {
    if (getUrlParameter(window.location.href, `show-${formType}`) === 'true') {
      if (region !== 'header') {
        setTimeout(() => {
          setShowModal(true);
        }, 500);
      }
    }
  }, [formType, region]);

  /**
   * Listens for modal closing.
   */
  useEffect(() => {
    if (!showModal) {
      closeForm(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Render.
   */
  return (
    <>
      {/* Form Button */}
      {!hideLink && (
        <button
          type="button"
          onClick={(e) => openForm(e)}
          className={
            linkClasses?.includes('btn')
              ? linkClasses
              : `${linkClasses} simple-modal-link`
          }>
          {buttonText}
        </button>
      )}

      {/* Form Modal  */}
      <ModalWrapper
        showModal={showModal}
        setShowModal={setShowModal}
        className={'modal-width-80'}>
        <form
          aria-label={title}
          onSubmit={handleSubmit(submitForm)}
          id={formType}>
          {/* Submission Message */}

          {submissionSuccessful && <FormResponse message={submitResponse} />}

          {/* Form */}
          {!submissionSuccessful && (
            <div
              className={
                submissionSuccessful ? `form-container hide` : `form-container`
              }>
              <h2>{title}</h2>

              {formDescription && (
                <p>
                  <Translate>{formDescription}</Translate>
                </p>
              )}

              <FormFields
                fieldList={formFields}
                register={register}
                errors={errors}
              />

              {repeatingMin && repeatingMin > 0 && (
                <RepeatingFieldCollection
                  title={repeatingTitle}
                  addButtonText={repeatingAddButtonText}
                  removeButtonText={repeatingRemoveButtonText}
                  fieldCollection={repeatingFields}
                  min={repeatingMin}
                  max={repeatingMax}
                  register={register}
                  errors={errors}
                  repeatingArrayKey={repeatingArrayKey}
                />
              )}

              {productFormType && (
                <input
                  type="hidden"
                  name="Products_of_Interest__c"
                  id="Products_of_Interest__c"
                  value={productFormType}
                />
              )}

              {safeCurrentLanguage() === 'zh-hans' ? (
                <ChineseDisclaimer register={register} errors={errors} />
              ) : (
                <PrivacyDisclaimer register={register} errors={errors} />
              )}

              {formType === 'careers-form' && (
                <CareersDisclaimer register={register} errors={errors} />
              )}

              <button
                value={submit}
                type="submit"
                className="btn-submit"
                formNoValidate>
                {submit}
              </button>
            </div>
          )}
        </form>
      </ModalWrapper>
    </>
  );
};

export default ModalFormContainer;
